// Signals are reported by severity rather than raw weight. The scoring weights are the
// core of the detection engine; publishing exact point values let anyone reconstruct the
// model by scanning controlled inputs. Users still see every signal and its explanation.
const SEVERITY_STYLES = {
  high: { label: 'High', className: 'bg-red-500/20 text-red-400' },
  medium: { label: 'Medium', className: 'bg-amber-500/20 text-amber-400' },
  low: { label: 'Low', className: 'bg-yellow-500/15 text-yellow-300' },
  context: { label: 'Context', className: 'bg-white/5 text-gray-400' },
  positive: { label: 'Reassuring', className: 'bg-emerald-500/20 text-emerald-400' },
}

export default function ScanResult({ result }) {
  const getRiskTier = (score) => {
    if (score <= 3) return {
      label: 'Safe',
      color: 'emerald',
      glow: 'shadow-glow-success',
      barBg: 'bg-emerald-500',
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-400',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      recommendation: 'Appears legitimate. Stay vigilant — never share sensitive documents or banking info upfront.'
    }
    if (score <= 6) return {
      label: 'Suspicious',
      color: 'amber',
      glow: 'shadow-glow-warning',
      barBg: 'bg-amber-500',
      border: 'border-amber-500/30',
      bg: 'bg-amber-500/10',
      text: 'text-amber-400',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      recommendation: 'Proceed with caution. Verify through official company channels before sharing any personal information.'
    }
    return {
      label: score >= 9 ? 'Confirmed Scam' : 'High Risk',
      color: 'red',
      glow: 'shadow-glow-danger',
      barBg: 'bg-red-500',
      border: 'border-red-500/30',
      bg: 'bg-red-500/10',
      text: 'text-red-400',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      ),
      recommendation: 'Do not engage. Block and report this contact immediately. Do not click any links or share any information.'
    }
  }

  const tier = getRiskTier(result.risk_score)
  const scorePercent = result.risk_score * 10

  return (
    <div className="space-y-4 animate-fade-in">

      {/* Risk Score Hero Card */}
      <div className={`card border ${tier.border} ${tier.glow} relative overflow-hidden`}>
        {/* Subtle background glow */}
        <div className={`absolute inset-0 ${tier.bg} opacity-50 pointer-events-none`} />

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-xl font-bold text-white">Scan Result</h3>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${tier.bg} border ${tier.border}`}>
              <span className={tier.text}>{tier.icon}</span>
              <span className={`text-sm font-bold ${tier.text}`}>{tier.label}</span>
            </div>
          </div>

          {/* Score display */}
          <div className="flex items-end gap-4 mb-4">
            <div>
              <span className={`text-6xl font-bold ${tier.text}`} style={{ fontFamily: "'Orbitron', sans-serif" }}>
                {result.risk_score}
              </span>
              <span className="text-2xl text-gray-500 font-bold">/10</span>
            </div>
            <p className="text-gray-400 text-sm pb-2 leading-relaxed max-w-xs">
              Risk score based on AI analysis, DNS verification, and community intelligence.
            </p>
          </div>

          {/* Score bar */}
          <div className="w-full bg-white/5 rounded-full h-2.5 overflow-hidden">
            <div
              className={`${tier.barBg} h-2.5 rounded-full transition-all duration-700 ease-out`}
              style={{ width: `${scorePercent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>Safe</span>
            <span>Suspicious</span>
            <span>Critical</span>
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      <div className="card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-cyan-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h4 className="font-bold text-white">AI Analysis</h4>
        </div>
        <p className="text-gray-300 leading-relaxed text-sm">{result.ai_summary}</p>
      </div>

      {/* Detection Signals */}
      {result.internal_analysis && result.internal_analysis.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-purple-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h4 className="font-bold text-white">Detection Signals</h4>
            <span className="ml-auto text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-full">
              {result.internal_analysis.length} signals
            </span>
          </div>
          <div className="space-y-2">
            {result.internal_analysis.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2.5 px-3 bg-white/3 hover:bg-white/5 rounded-lg border border-white/5 transition-colors"
              >
                <span className="text-sm text-gray-300">{item.flag}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${SEVERITY_STYLES[item.severity]?.className || 'bg-white/5 text-gray-400'}`}>
                  {SEVERITY_STYLES[item.severity]?.label || 'Noted'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Saved */}
      {result.time_saved_seconds > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-cyan-500/5 border border-cyan-500/15 rounded-xl">
          <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-cyan-300">
            Scan saved you approximately{' '}
            <strong className="text-cyan-200">{Math.round(result.time_saved_seconds / 60)} minutes</strong>{' '}
            of manual research
          </span>
        </div>
      )}

      {/* Recommendation */}
      <div className={`card border ${tier.border} ${tier.bg}`}>
        <div className="flex items-start gap-3">
          <span className={`${tier.text} mt-0.5 flex-shrink-0`}>{tier.icon}</span>
          <div>
            <h4 className={`font-bold ${tier.text} mb-1`}>Recommendation</h4>
            <p className="text-gray-300 text-sm leading-relaxed">{tier.recommendation}</p>
          </div>
        </div>
      </div>

    </div>
  )
}
